import { Controller } from "@hotwired/stimulus"

// Disables a submit button while its form submission is in flight.
export default class extends Controller {
  static values = {
    disabledClass: { type: String, default: "opacity-60 cursor-not-allowed" }
  }

  connect() {
    this.pending = false
    this.disableFrame = null

    this.boundSubmitStart = this.handleSubmitStart.bind(this)
    this.boundSubmitEnd = this.handleSubmitEnd.bind(this)
    this.boundNativeSubmit = this.handleNativeSubmit.bind(this)
    this.boundInvalid = this.handleInvalid.bind(this)
    this.boundClick = this.handleClick.bind(this)
    this.form = this.element.form || this.element.closest("form")

    this.element.addEventListener("click", this.boundClick)

    if (this.form) {
      this.form.addEventListener("turbo:submit-start", this.boundSubmitStart)
      this.form.addEventListener("turbo:submit-end", this.boundSubmitEnd)
      this.form.addEventListener("submit", this.boundNativeSubmit)
      this.form.addEventListener("invalid", this.boundInvalid, true)
    }
  }

  disconnect() {
    this.element.removeEventListener("click", this.boundClick)

    if (this.disableFrame) {
      cancelAnimationFrame(this.disableFrame)
      this.disableFrame = null
    }

    if (!this.form) return

    this.form.removeEventListener("turbo:submit-start", this.boundSubmitStart)
    this.form.removeEventListener("turbo:submit-end", this.boundSubmitEnd)
    this.form.removeEventListener("submit", this.boundNativeSubmit)
    this.form.removeEventListener("invalid", this.boundInvalid, true)
  }

  handleClick() {
    if (this.element.disabled) return

    this.pending = true
    this.scheduleDisable()
  }

  handleSubmitStart(event) {
    if (!this.pending && !this.shouldHandleEvent(event?.detail?.submitter)) return

    this.pending = true
    this.disableButton()
  }

  handleNativeSubmit(event) {
    if (!this.pending && !this.shouldHandleEvent(event.submitter)) return

    this.pending = true
    this.disableButton()
  }

  handleSubmitEnd(event) {
    if (!this.pending && !this.shouldHandleEvent(event?.detail?.submitter)) return

    this.pending = false
    this.enableButton()
  }

  handleInvalid() {
    if (!this.pending) return

    this.pending = false
    this.enableButton()
  }

  shouldHandleEvent(submitter) {
    if (submitter) {
      return submitter === this.element
    }

    // Fallback for browsers that don't yet support SubmitEvent#submitter
    return document.activeElement === this.element
  }

  disableButton() {
    if (this.element.disabled) return

    if (this.disableFrame) {
      cancelAnimationFrame(this.disableFrame)
      this.disableFrame = null
    }

    this.element.disabled = true
    this.applyDisabledClasses()
  }

  enableButton() {
    this.element.disabled = false
    this.removeDisabledClasses()
  }

  applyDisabledClasses() {
    this.disabledClassValue
      .split(" ")
      .filter((className) => className.trim().length > 0)
      .forEach((className) => this.element.classList.add(className.trim()))
  }

  removeDisabledClasses() {
    this.disabledClassValue
      .split(" ")
      .filter((className) => className.trim().length > 0)
      .forEach((className) => this.element.classList.remove(className.trim()))
  }

  scheduleDisable() {
    if (this.disableFrame) return

    this.disableFrame = requestAnimationFrame(() => {
      this.disableFrame = null

      if (this.pending) {
        this.disableButton()
      }
    })
  }
}
